import {
  Component,
  ViewEncapsulation,
  ElementRef,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnInit,
  OnDestroy,
  TemplateRef,
  Output,
  EventEmitter,
  afterNextRender,
  inject,
} from '@angular/core';
import { ModalService } from '~shared/services/modal/modal.service';
import { DialogFocusService } from '~shared/services/accessibility/dialog-focus.service';
import { isRepeatedClick } from '~shared/utils/mouse-event.utils';

/** Classe du backdrop plein écran (cf. modal.component.html / .scss). */
const BACKDROP_CLASS = 'jw-modal';

@Component({
  selector: 'app-jw-modal',
  templateUrl: 'modal.component.html',
  styleUrls: ['modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ModalComponent implements OnInit, OnDestroy {
  private modalService = inject(ModalService);
  private dialogFocus = inject(DialogFocusService);
  private el = inject(ElementRef);
  private injecteur = inject(Injector);

  @Output() cancelAct = new EventEmitter();
  @Input() id?: string;
  @HostBinding('attr.id') get hostId(): string | undefined {
    return this.id;
  }
  @Input() type?: string;
  @Input() content: TemplateRef<any>;
  @Input() title: string;
  /**
   * `title` est une ENTRÉE (clé i18n du titre de la modale) : écrit en
   * attribut statique (`title="MODAL.…"`), il resterait aussi sur l'hôte et
   * deviendrait l'infobulle de toute la modale — la clé brute au survol.
   */
  @HostBinding('attr.title') readonly hostTitle = null;
  @Input() class: string;

  isOpen = false;
  private element: any;

  constructor() {
    this.element = this.el.nativeElement;
  }

  ngOnInit() {
    // add self (this modal instance) to the modal service so it can be opened from any component
    this.modalService.add(this);

    // move element to bottom of page (just before </body>) so it can be displayed above everything else
    document.body.appendChild(this.element);

    // close modal on background click
    this.element.addEventListener('mousedown', (event: MouseEvent) => {
      if (this.isBackdropClick(event)) {
        this.close();
        this.cancelAct.emit();
      }
    });
  }

  /**
   * Vrai si le `mousedown` est un clic volontaire sur le backdrop.
   *
   * Le 2ᵉ `mousedown` d'un double clic sur le bouton qui vient d'ouvrir la
   * modale tombe sur le backdrop fraîchement affiché (le bouton est recouvert) :
   * il est ignoré (`isRepeatedClick`), sinon la modale se refermerait aussitôt.
   */
  private isBackdropClick(event: MouseEvent): boolean {
    const target = event.target as HTMLElement | null;
    return target?.className === BACKDROP_CLASS && !isRepeatedClick(event);
  }

  /**
   * Échap ferme la modale (RGAA 7.3, modèle ARIA « dialog »).
   *
   * Le volet latéral le faisait déjà ; la modale, non — elle ne se fermait
   * qu'à la souris, sur son bouton ou son fond. Au clavier, elle était donc
   * un piège : le focus y est retenu à dessein, sans moyen d'en sortir.
   *
   * `cancelAct` est émis comme pour un clic sur le fond : 49 gabarits s'en
   * servent pour remettre leur propre état à zéro, et une fermeture qui ne
   * l'émettrait pas les laisserait croire la modale encore ouverte.
   *
   * L'écoute est posée sur le `document` parce que le focus peut être sur
   * n'importe quel descendant ; c'est le service qui désigne laquelle répond
   * quand plusieurs sont ouvertes.
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(evenement?: Event): void {
    if (!this.modalService.reclamerEchap(this, evenement)) {
      return;
    }
    this.close();
    this.cancelAct.emit();
  }

  /** Le nœud est-il dans cette modale ? Utilisé par l'arbitre d'Échap. */
  contient(noeud: Node): boolean {
    return (this.element as HTMLElement).contains(noeud);
  }

  ngOnDestroy() {
    // remove self from modal service
    this.modalService.remove(this);

    // remove modal element from html
    this.element.remove();
  }

  open() {
    this.element.style.display = 'block';
    document.body.classList.add('jw-modal-open');
    this.isOpen = true;

    // Le focus est piege dans la fenetre et rendu a son declencheur a la
    // fermeture (RGAA 12.8). Appele ici plutot que dans une directive : c'est
    // l'instant exact ou la fenetre sait qu'elle s'ouvre.
    //
    // APRES LE RENDU, et c'est la condition de tout le reste : demasquer la
    // fenetre ne suffit pas, son CONTENU n'existe pas encore. Les appelants
    // posent le type de fenetre puis appellent `open()` dans le MEME tick
    // (`content.component.ts` : `volet.ouvre(sorte)` suivi de
    // `modalService.open(...)`), et le gabarit place ce contenu derriere un
    // `@if (modalType())` : au moment de l'appel, le corps de la fenetre est
    // vide. Le piege du CDK n'y trouvait alors AUCUN element focalisable, le
    // focus restait sur le declencheur, et l'utilisateur au clavier tabulait
    // dans la page derriere la fenetre — mesure par le parcours clavier de
    // bout en bout : huit tabulations sur huit hors de la fenetre.
    //
    // Meme raison et meme remede que le volet lateral, qui capture depuis
    // `afterNextRender` (cf. `slide-in.component.ts` et le commentaire de
    // `DialogFocusService.capture`).
    afterNextRender(
      () => {
        if (this.isOpen) {
          this.dialogFocus.capture(this.element as HTMLElement);
        }
      },
      { injector: this.injecteur }
    );
  }

  close() {
    this.element.style.display = 'none';
    document.body.classList.remove('jw-modal-open');
    this.isOpen = false;
    this.dialogFocus.release(this.element as HTMLElement);
  }

  onConfirm() {
    this.close();
  }

  onCancel() {
    this.close();
  }
}
